import { createContext, useContext } from 'react';
import type { Project, TargetGroup } from '../../types';

export interface WizardValue {
  project: Project;
  targetGroup: TargetGroup;
  reload: () => Promise<void>;
  goStep: (step: string, opts?: { force?: boolean }) => void;
  setLiveBasics: (values: BasicsValues | null) => void;
  setDirty: (dirty: boolean) => void;
}

/** The Basics form's values (strings from the inputs); see basicsModel.js. */
export interface BasicsValues {
  startDate: string;
  endDate: string;
  country_id: string;
  ageMinLimit: string;
  ageMaxLimit: string;
  gender: string;
  estimatedLenghthOfInterview: string; // sic: spelled this way in the backend model
  noOfWantedCompletes: string;
  estimatedIncidenceRate: string;
}

/**
 * Shared by the wizard steps:
 *  project, targetGroup  current data from the server
 *  reload()              re-fetch the project after a save
 *  goStep(step, opts)    navigate to another step of this target group; asks first when the current step has
 *                        unsaved changes (see setDirty), unless opts.force is true (used right after a save)
 *  setDirty(bool)        a step reports whether it holds unsaved changes
 *  setLiveBasics(values) lets the Basics form feed the live feasibility panel
 */
export const WizardContext = createContext<WizardValue | null>(null);

export const useWizard = (): WizardValue => {
  const ctx = useContext(WizardContext);
  if (!ctx) throw new Error('useWizard must be used inside <TargetGroupWizard>');
  return ctx;
};
